import { useEffect, useRef } from 'react';
import { useAppSelector } from '../store/hooks';
import { runAndSaveSpeedTest } from '../services/networkSpeedService';

/**
 * Runs the network speed test automatically, in the background, for as long
 * as the user has an active session — no "Run Speed Test" click required.
 *
 * Mounted once at the app root (see App.js) so it survives route/tab
 * navigation instead of resetting every time the Dashboard remounts.
 *
 * Timing is randomized (not a fixed poll) so:
 *   - many staff sessions don't all hit the server in the same instant, and
 *   - the fluctuation graph gets samples spread naturally across the day
 *     instead of one suspiciously-regular heartbeat.
 */
const FIRST_RUN_MIN_MS = 3 * 60 * 1000; // 3 min
const FIRST_RUN_MAX_MS = 10 * 60 * 1000; // 10 min
const REPEAT_MIN_MS = 20 * 60 * 1000; // 20 min
const REPEAT_MAX_MS = 45 * 60 * 1000; // 45 min
const HIDDEN_RETRY_MS = 60 * 1000; // re-check every minute while the tab is backgrounded

// Lighter payload than the manual "Run Speed Test" button — this runs many
// times a day per user, so keep it bandwidth-considerate while still
// producing a usable Mbps estimate.
const AUTO_TEST_OPTS = { downloadSizeKb: 512, uploadSizeKb: 256, pingCount: 3 };

// Shared across tabs in the same browser so two open tabs for the same user
// don't both fire a test in the same window.
const LAST_RUN_KEY = 'pos_last_auto_speedtest_at';
const MIN_GAP_ACROSS_TABS_MS = REPEAT_MIN_MS;

const randomBetween = (min, max) => min + Math.random() * (max - min);

export default function useBackgroundSpeedTest() {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const defaultWarehouse = useAppSelector((state) => state.auth.user?.default_warehouse);
  const warehouseRef = useRef(defaultWarehouse);
  const timerRef = useRef(null);
  const stoppedRef = useRef(true);

  useEffect(() => {
    warehouseRef.current = defaultWarehouse;
  }, [defaultWarehouse]);

  useEffect(() => {
    if (!isAuthenticated) {
      stoppedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      return undefined;
    }

    stoppedRef.current = false;

    const scheduleNext = () => {
      if (stoppedRef.current) return;
      const delay = randomBetween(REPEAT_MIN_MS, REPEAT_MAX_MS);
      timerRef.current = setTimeout(runOnce, delay);
    };

    // eslint-disable-next-line no-inner-declarations
    async function runOnce() {
      if (stoppedRef.current) return;

      // Skip while backgrounded — a hidden tab isn't representative of the
      // connection actually in use, and browsers throttle timers there anyway.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        timerRef.current = setTimeout(runOnce, HIDDEN_RETRY_MS);
        return;
      }

      const lastRun = Number(localStorage.getItem(LAST_RUN_KEY) || 0);
      if (Date.now() - lastRun < MIN_GAP_ACROSS_TABS_MS) {
        scheduleNext();
        return;
      }

      try {
        await runAndSaveSpeedTest({
          ...AUTO_TEST_OPTS,
          warehouse: warehouseRef.current,
          triggerSource: 'Auto',
        });
        localStorage.setItem(LAST_RUN_KEY, String(Date.now()));
      } catch (err) {
        // Silent — this is background telemetry, not a user-facing action.
      }
      scheduleNext();
    }

    timerRef.current = setTimeout(runOnce, randomBetween(FIRST_RUN_MIN_MS, FIRST_RUN_MAX_MS));

    return () => {
      stoppedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated]);
}
